import React, { useCallback, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { WORKFLOW_INBOX_PERMISSIONS } from '../../config/workflowCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWorkflowInbox } from '../../hooks/useWorkflowInbox';
import type { WorkflowInboxItem } from '../../types/workflow';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatAttendanceTimestamp } from '../../utils/dateTime';
import { getWorkflowApprovalRoute } from '../../utils/workflowRequestType';

type Props = NativeStackScreenProps<RootStackParamList, 'WorkflowInbox'>;

function InboxRow({
  item,
  onPress,
  palette,
}: {
  item: WorkflowInboxItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const employeeLabel =
    item.employee.displayName ||
    item.employee.employeeNumber ||
    'Employee unavailable';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${item.requestTypeLabel}, ${employeeLabel}, ${item.workflowStatusLabel}`}
      onPress={onPress}
      style={[
        styles.row,
        {
          backgroundColor: palette.surface,
          borderColor: palette.border,
        },
      ]}
    >
      <Text style={[styles.title, { color: palette.textPrimary }]}>
        {item.requestTypeLabel}
      </Text>
      <Text style={[styles.meta, { color: palette.textSecondary }]}>
        {item.requestNumber || `Workflow #${item.workflowInstanceId}`}
      </Text>
      <Text style={[styles.meta, { color: palette.textSecondary }]}>
        {employeeLabel}
      </Text>
      {item.employee.employeeNumber && item.employee.displayName ? (
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {item.employee.employeeNumber}
        </Text>
      ) : null}
      {item.requestSummary ? (
        <Text
          style={[styles.meta, { color: palette.textSecondary }]}
          numberOfLines={2}
        >
          {item.requestSummary}
        </Text>
      ) : null}
      <Text style={[styles.meta, { color: palette.textSecondary }]}>
        Submitted: {formatAttendanceTimestamp(item.submittedAt, null)}
      </Text>
      {item.currentStepName ? (
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Step: {item.currentStepName}
        </Text>
      ) : null}
      <Text style={[styles.status, { color: palette.textPrimary }]}>
        Status: {item.workflowStatusLabel}
      </Text>
    </Pressable>
  );
}

export function WorkflowInboxScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const inbox = useWorkflowInbox();
  const {
    applyFilters,
    loadInbox,
    refreshInbox,
    resetFilters,
    retry,
    filters,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
    totalItems,
  } = inbox;
  const hasFocused = useRef(false);

  useFocusEffect(
    useCallback(() => {
      const applied = route.params?.appliedFilters;
      if (applied !== undefined) {
        applyFilters(applied);
        navigation.setParams({ appliedFilters: undefined });
        hasFocused.current = true;
        return;
      }
      if (hasFocused.current) return;
      hasFocused.current = true;
      loadInbox();
    }, [
      applyFilters,
      loadInbox,
      navigation,
      route.params?.appliedFilters,
    ]),
  );

  const filtersCustomized = Boolean(filters.entityType);

  const themed = useMemo(
    () => ({
      range: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.sm,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footer} accessibilityLabel="Loading more approvals">
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  return (
    <PermissionGuard
      anyPermissions={WORKFLOW_INBOX_PERMISSIONS}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Approvals"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          {totalItems != null ? (
            <Text style={themed.range}>Pending: {totalItems}</Text>
          ) : (
            <Text style={themed.range}>Pending approval inbox</Text>
          )}
          {filters.entityType ? (
            <Text style={themed.range}>Type: {formatDisplayLabel(filters.entityType)}</Text>
          ) : null}
          <View style={styles.actions}>
            <AppButton
              title="Filters"
              variant="secondary"
              onPress={() =>
                navigation.navigate(routes.workflowFilter, {
                  currentFilters: filters,
                })
              }
            />
          </View>
          <Text style={themed.warning}>
            Open a request to approve or reject it.
          </Text>

          {isLoading && items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading approvals"
              />
            </View>
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.workflowInstanceId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshInbox}
                  tintColor={palette.primary}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No pending approvals"
                  message="There are no workflow requests waiting for you."
                  actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                  onAction={filtersCustomized ? resetFilters : retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <InboxRow
                  item={item}
                  onPress={() => {
                    const approvalRoute = getWorkflowApprovalRoute(
                      item.requestTypeCode,
                    );
                    if (approvalRoute === 'leaveApproval') {
                      navigation.navigate(routes.leaveApproval, {
                        workflowInstanceId: item.workflowInstanceId,
                      });
                      return;
                    }
                    if (approvalRoute === 'wfhApproval') {
                      navigation.navigate(routes.wfhApproval, {
                        workflowInstanceId: item.workflowInstanceId,
                      });
                      return;
                    }
                    if (approvalRoute === 'timePermissionApproval') {
                      navigation.navigate(routes.timePermissionApproval, {
                        workflowInstanceId: item.workflowInstanceId,
                      });
                      return;
                    }
                    navigation.navigate(routes.workflowDetails, {
                      workflowInstanceId: item.workflowInstanceId,
                    });
                  }}
                  palette={palette}
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  row: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.lg,
    gap: spacing.xs,
  },
  title: { ...typography.body, fontWeight: '600' },
  meta: { ...typography.caption },
  status: { ...typography.caption },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
